<template>
  <div class="lookup-page">
    <v-card class="lookup-card">
      <div class="lookup-header">
        <div class="lookup-header__copy">
          <div class="lookup-title">Health Professions</div>
          <div class="lookup-subtitle">Maintain health profession definitions and codes.</div>
        </div>
        <v-text-field
            v-model="search"
            class="lookup-search"
            append-icon="mdi-magnify"
            hide-details
            label="Search"
            single-line
        ></v-text-field>
        <v-btn
            v-if="canCreateHProfessions"
            class="lookup-add-button"
            depressed
            @click="addHealthProfession()"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          <span>Add Health Profession</span>
        </v-btn>
      </div>
      <v-data-table
          :headers="headers"
          :items="healthProfessions.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="healthProfessions.count"
          class="lookup-table elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.name`]="{ item }">
          <label for="item">{{ item.name ? item.name : "-" }}</label>
        </template>
        <template v-slot:[`item.code`]="{ item }">
          <label for="item">{{ item.code ? item.code : "-" }}</label>
        </template>
        <template v-if="canEditHProfessions" v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                  class="mr-2 lookup-action-icon"
                  @click="editHealthProfession(item)"
                  v-on="on"
              >mdi-circle-edit-outline
              </v-icon
              >
            </template>
            <span>Edit Health Profession</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.isActive`]="{ item }">
          <v-chip :color="getColor(item.isActive)" dark>
            {{ item.isActive == true ? "Active" : "Not Active" }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="dialog" persistent width="50%">
        <v-card class="lookup-dialog-card">
          <v-card-title
              class="lookup-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="lookup-dialog__close" color="white" text @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <HrdAddHealthProfession
              v-if="dialog"
              :healthProfession="healthProfession"
              @close="closeDialog()"
              @registerOrUpdateHealthProf="registerOrUpdateHealthProf"
          ></HrdAddHealthProfession>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import HrdAddHealthProfession from "./hrd-add-health-profession.vue";
import {LookupService} from "../../../common/api.service";
import {HealthProfessionService} from "./healthProfession.service";
import {getUser} from "@/utils/helper";

export default {
  components: {
    HrdAddHealthProfession,
  },
  data() {
    return {
      search: "",
      searchDebounce: null,
      loading: false,
      headers: [
        {text: "Health Profession Name", value: "name"},
        {text: "Health Profession Code", value: "code"},
        {text: "Health Profession Status", value: "isActive"},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      healthProfessions: [],
      healthProfession: null,
      dialog: false,
      title: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
    };
  },
  async beforeMount() {
    this.fetchHealthProfessions();
  },
  watch: {
    options: {
      handler() {
        this.fetchHealthProfessions();
      },
      deep: true,
    },
    search: function (val) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const text = (val || "").toString().trim();
      if (text.length === 0) {
        this.fetchHealthProfessions();
        return;
      }
      if (text.length < 3) {
        return;
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchHealthProfessions();
      }, 400);
    },
  },
  computed: {
    canCreateHProfessions() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Health Professions" && permission.canCreate
      );
    },
    canEditHProfessions() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Health Professions" && permission.canUpdate
      );
    },
  },
  methods: {
    async fetchHealthProfessions() {
      this.loading = true;
      const {page, itemsPerPage} = this.options;
      const res = await LookupService.getHealthProffesionsWP(
          page,
          itemsPerPage,
          this.search
      );
      if (res.status === 200) {
        this.loading = false;
        this.healthProfessions = res.data;
      } else {
        this.loading = false;
        this.$toast.error(
            "There was a problem fetching health professions, Please try Again!"
        );
      }
    },
    addHealthProfession() {
      this.healthProfession = null;
      this.dialog = true;
      this.title = "Add Health Profession";
    },
    editHealthProfession(item) {
      this.healthProfession = item;
      this.dialog = true;
      this.title = "Edit Health Profession";
    },
    closeDialog() {
      this.dialog = false;
      this.fetchHealthProfessions();
    },
    getColor(isActive) {
      if (isActive == false) return "red";
      else return "green";
    },
    async registerOrUpdateHealthProf(healthProfession) {
      this.loading = true;
      let res = null;

      if (healthProfession.editMode) {
        res = await HealthProfessionService.update(
            healthProfession.id,
            healthProfession
        );

        if (res.status === 201) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fetchHealthProfessions();
              this.loading = false;
              break;
            case 500:
              this.$toast.error(res.data.message);
              this.loading = false;
              break;
          }
        } else {
          this.$toast.error(
              "Unable to update health profession, please try again"
          );
          this.loading = false;
        }
      } else {
        res = await HealthProfessionService.create(healthProfession);

        if (res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fetchHealthProfessions();
              this.loading = false;

              break;
            case 500:
              this.$toast.error(res.data.message);
              this.loading = false;

              break;
          }
        } else {
          this.$toast.error(
              "Unable to create health profession, please try again"
          );
          this.loading = false;
        }
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.lookup-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.lookup-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.lookup-header {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 360px) auto;
  align-items: center;
  gap: 16px;
  padding: 24px 28px;
}

.lookup-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.lookup-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.lookup-subtitle {
  font-size: 14px;
  color: #475569;
}

.lookup-search {
  width: 100%;
  max-width: 360px;
  justify-self: center;
  align-self: center;
}

.lookup-table {
  border-radius: 0 0 12px 12px;
}

.lookup-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.lookup-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #3a4f63;
  color: #ffffff;
  padding: 20px 24px;
}

.lookup-dialog__close {
  color: #ffffff !important;
}

.lookup-dialog__close .v-icon {
  color: #ffffff !important;
}

.lookup-add-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.lookup-action-icon {
  color: #1976d2 !important;
}

@media (max-width: 700px) {
  .lookup-page {
    padding: 12px;
  }

  .lookup-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .lookup-search {
    max-width: 100%;
  }

  .lookup-add-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
